import { useQuery } from "@tanstack/react-query"

import { History } from "lucide-react"

import preCheckFraudFlagHistoryQuery from "../../services/pre-check-fraud-flag-history"
import FraudTypeChips from "../FraudTypeChips"

const formatDateTime = (date) => {
    if (!date) return ""
    return new Date(date).toLocaleString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    })
}

const StatusBadge = ({ flagged }) => (
    flagged ? (
        <span className="inline-flex items-center px-2 py-0.5 rounded border border-red-200 bg-red-50 text-[11px] font-semibold text-red-700">
            Potential Fraud
        </span>
    ) : (
        <span className="inline-flex items-center px-2 py-0.5 rounded border border-gray-200 bg-gray-100 text-[11px] font-semibold text-gray-700">
            Not Flagged
        </span>
    )
)

const ACTION_LABELS = {
    flagged: "Marked as Potential Fraud",
    unflagged: "Flag Removed",
    // Saving the status it already has (only possible while flagged) records new fraud types / explanation.
    comment: "Fraud Status Updated",
}

// The potential fraud history, fetched when the section is expanded: the CURRENT status with its
// LATEST explanation first, then every status change / explanation update (newest first) with who
// and when.
// `refreshKey` bumps after an edit to refetch.
const FraudFlagHistory = ({ preCheckId, refreshKey }) => {
    const { data: entries, isFetching } = useQuery({
        queryKey: ["pre-check-fraud-flag-history", preCheckId, refreshKey],
        queryFn: () => preCheckFraudFlagHistoryQuery(preCheckId),
        refetchOnWindowFocus: false,
        enabled: !!preCheckId,
    })

    if (isFetching && !entries) {
        return <p className="text-sm text-gray-500">Loading potential fraud history...</p>
    }

    if (!entries?.length) {
        return (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-semibold text-gray-700">Current Status</p>
                    <StatusBadge flagged={false} />
                </div>
                <p className="text-sm text-gray-500">This TruCheck has never been flagged as potential fraud.</p>
            </div>
        )
    }

    // Entries are newest first. The current STATUS comes from the latest status change (flag / unflag);
    // the current EXPLANATION is the latest entry of any kind — re-saving the same status (e.g. setting
    // "Potential Fraud" again with a new explanation) records an explanation update, not a status change,
    // and that newest explanation is what the current status shows.
    const currentStateEntry = entries.find((entry) => entry.action === "flagged" || entry.action === "unflagged")
    const latestEntry = entries[0]
    const currentlyFlagged = !!currentStateEntry?.flagged
    const explanationIsNewer = latestEntry && currentStateEntry && latestEntry._id !== currentStateEntry._id

    return (
        <div className="space-y-4">
            <div className={`rounded-xl border p-4 ${currentlyFlagged ? "border-red-200 bg-red-50" : "border-gray-200 bg-gray-50"}`}>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                    <p className="text-sm font-semibold text-gray-700">Current Status</p>
                    <StatusBadge flagged={currentlyFlagged} />
                </div>
                {latestEntry && (
                    <>
                        {currentlyFlagged && <FraudTypeChips fraudTypes={latestEntry.fraudTypes} className="mb-2" />}
                        <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">{latestEntry.comment}</p>
                        <p className="text-xs text-gray-500 mt-2">
                            {ACTION_LABELS[latestEntry.action] || "Fraud Status Updated"}
                            {latestEntry.updatedByName && <> by <span className="font-semibold">{latestEntry.updatedByName}</span></>}
                            {" "}on {formatDateTime(latestEntry.createdAt)}
                        </p>
                        {explanationIsNewer && (
                            <p className="text-xs text-gray-500 mt-0.5">
                                {currentlyFlagged ? "Flagged as Potential Fraud" : "Flag removed"}
                                {currentStateEntry.updatedByName && <> by <span className="font-semibold">{currentStateEntry.updatedByName}</span></>}
                                {" "}on {formatDateTime(currentStateEntry.createdAt)}
                            </p>
                        )}
                    </>
                )}
            </div>

            <div>
                <div className="flex items-center gap-2 mb-2">
                    <History className="w-4 h-4 text-gray-400" />
                    <p className="text-sm font-semibold text-gray-700">History</p>
                </div>
                <ul className="space-y-2">
                    {entries.map((entry, i) => (
                        <li key={entry._id ?? i} className="p-3 rounded-lg bg-gray-50 border border-gray-100">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                                <StatusBadge flagged={!!entry.flagged} />
                                <span className="text-xs font-semibold text-gray-700">{ACTION_LABELS[entry.action] || "Fraud Status Updated"}</span>
                                <span className="text-xs text-gray-500">
                                    {formatDateTime(entry.createdAt)}
                                    {entry.updatedByName && <> · by {entry.updatedByName}</>}
                                </span>
                            </div>
                            {entry.flagged && <FraudTypeChips fraudTypes={entry.fraudTypes} className="mb-1" />}
                            <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">{entry.comment}</p>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    )
}

export default FraudFlagHistory
